import { brl } from "@/lib/api";
import { code128Svg, code128WidthMm } from "@/lib/barcode";

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

/**
 * Etiqueta de produto para impressora de adesivo.
 *
 * O agente já sabia imprimir no perfil "etiqueta" — faltava alguém gerar o que
 * imprimir. O tamanho vem do perfil configurado naquela máquina (a etiquetadora
 * do balcão pode ser 60×40, a do depósito 50×30), então o HTML se adapta em vez
 * de fixar medidas.
 *
 * O que entra numa etiqueta e por quê: o código de barras é o que faz o PDV
 * achar o produto sem digitar; o preço é o que o cliente procura na prateleira;
 * o nome existe para conferência humana e é a primeira coisa a ser cortada
 * quando o espaço aperta.
 */

/**
 * Módulo (barra mais fina) que faz o código caber na largura útil.
 *
 * Começa no ideal e só reduz se precisar, nunca abaixo de 0,25mm — abaixo disso
 * a tinta da térmica espalha, as barras se fundem e o código para de ler. Se nem
 * assim couber, é melhor sair um pouco largo e ser aparado do que sair ilegível.
 */
function moduloQueCabe(codigo, larguraUtilMm) {
  for (const m of [0.33, 0.3, 0.28, 0.26, 0.25]) {
    if (code128WidthMm(codigo, m) <= larguraUtilMm) return m;
  }

  return 0.25;
}

/**
 * @param {object} etiqueta  { name, variant_label, price, barcode, brand }
 * @param {object} papel     { width_mm, height_mm } — do perfil do agente
 */
export function buildLabelHtml(etiqueta, { width_mm: largura = 60, height_mm: altura = 40 } = {}) {
  const margem = 2;
  const util = Math.max(20, largura - margem * 2);
  const codigo = String(etiqueta.barcode || "").trim();
  const modulo = moduloQueCabe(codigo, util);

  // Etiqueta baixa (30mm ou menos) não comporta nome + variação + preço + barras:
  // o nome é o que sai, porque é o único que a pessoa consegue deduzir olhando o
  // produto na mão.
  const compacta = altura <= 30;
  const alturaBarras = compacta ? 9 : 12;

  return `<!doctype html><html><head><meta charset="utf-8"><style>
    @page { margin: 0; }
    *{margin:0;padding:0;box-sizing:border-box}
    html,body{width:${largura}mm;height:${altura}mm}
    body{
      font-family:-apple-system,"Segoe UI",Arial,sans-serif;color:#000;
      padding:${margem}mm;display:flex;flex-direction:column;
      align-items:center;justify-content:space-between;text-align:center;
      overflow:hidden;
    }
    /* Duas linhas no máximo, com reticências: nome comprido empurrando o preço
       para fora do adesivo é o defeito clássico de etiqueta. */
    .nome{
      font-size:${compacta ? 7 : 8}pt;line-height:1.15;font-weight:600;width:100%;
      display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;
    }
    .var{font-size:${compacta ? 6 : 7}pt;color:#333;line-height:1.1}
    .preco{font-size:${compacta ? 11 : 14}pt;font-weight:700;line-height:1;letter-spacing:-.02em}
    .cod{font-family:ui-monospace,"SF Mono",Menlo,monospace;font-size:${compacta ? 5.5 : 6.5}pt;letter-spacing:.06em;line-height:1}
    .barras{line-height:0}
    .sem{font-size:6pt;color:#555}
  </style></head><body>
    <div>
      ${compacta ? "" : `<div class="nome">${esc(etiqueta.name)}</div>`}
      ${etiqueta.variant_label && etiqueta.variant_label !== "Único"
        ? `<div class="var">${esc(etiqueta.variant_label)}</div>` : ""}
    </div>
    <div class="preco">${brl(etiqueta.price)}</div>
    <div>
      ${codigo
        ? `<div class="barras">${code128Svg(codigo, { heightMm: alturaBarras, moduleWidth: modulo })}</div>
           <div class="cod">${esc(codigo)}</div>`
        : `<div class="sem">Sem código de barras cadastrado</div>`}
    </div>
  </body></html>`;
}

/**
 * Folha com várias etiquetas, uma por página.
 *
 * `page-break-after` em vez de repetir o job: mandar N impressões separadas faz
 * a etiquetadora avançar e cortar entre cada uma, e em rolo contínuo isso
 * desperdiça um adesivo a cada peça. Uma folha só, com quebra de página, sai na
 * cadência que o papel espera.
 */
export function buildLabelSheetHtml(etiqueta, quantidade, papel) {
  const uma = buildLabelHtml(etiqueta, papel);
  if (quantidade <= 1) return uma;

  const corpo = uma.match(/<body>([\s\S]*)<\/body>/)?.[1] ?? "";
  const cabeca = uma.match(/<head>([\s\S]*)<\/head>/)?.[1] ?? "";
  const paginas = Array.from({ length: quantidade }, (_, i) =>
    `<div class="etq" style="${i < quantidade - 1 ? "page-break-after:always;" : ""}">${corpo}</div>`).join("");

  return `<!doctype html><html><head>${cabeca}<style>
    body{display:block;padding:0}
    .etq{
      width:${papel?.width_mm || 60}mm;height:${papel?.height_mm || 40}mm;
      padding:2mm;display:flex;flex-direction:column;
      align-items:center;justify-content:space-between;text-align:center;overflow:hidden;
    }
  </style></head><body>${paginas}</body></html>`;
}
